import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatearFecha, formatearFechaHora, ZONA_HORARIA } from '@cmms/shared';
import { api } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import {
  AccionesDialogo,
  CasillaVerificacion,
  Cargando,
  Dialogo,
  DialogoConfirmar,
  ErroresFormulario,
  Etiqueta,
  Vacio,
  mensajeError,
} from '../componentes/piezas.js';
import {
  SelectorFecha,
  SelectorLista,
  SelectorListaMultiple,
  SelectorUbicacionMultiple,
  useUbicaciones,
  useUsuariosOpciones,
} from '../componentes/selectores.js';
import { IconoLupa } from '../componentes/iconos.js';
import { BotonVolver } from '../componentes/boton-volver.js';
import { Indicador } from '../componentes/piezas.js';

interface InventarioResumen {
  id: string;
  nombre: string;
  descripcion: string | null;
  sedes: string[];
  revisiones: number;
  activosRegistrados: number;
  creadoEl: string;
  cerradoEl: string | null;
}

interface RevisionInventario {
  id: string;
  nombre: string;
  fecha: string;
  nota: string | null;
  activosRegistrados: number;
  creadoEl: string;
}

interface InventarioDetalle extends InventarioResumen {
  sedesDetalle: { id: string; nombre: string }[];
  revisionesDetalle: RevisionInventario[];
}

interface ActivoParaRevision {
  id: string;
  numero: number;
  nombre: string;
  ubicacionNombre: string | null;
  registradoEnRevision: boolean;
  ultimoRegistro: {
    inventarioNombre: string;
    revisionNombre: string;
    fecha: string;
    responsableNombre: string;
  } | null;
}

interface RegistroDeRevision {
  id: string;
  activoId: string;
  activoNumero: number;
  activoNombre: string;
  ubicacionNombre: string | null;
  responsableNombre: string;
  nota: string | null;
  registradoEl: string;
}

interface ResultadoRegistro {
  registrados: number;
  yaRegistrados: number;
}

const plural = (n: number, singular: string, plural: string): string => `${n} ${n === 1 ? singular : plural}`;

/** Hoy como AAAA-MM-DD en hora de Lima: en UTC, de noche en Perú ya sería el día siguiente. */
function hoyEnLima(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: ZONA_HORARIA, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

/** Lista de inventarios: cada uno cubre una o varias sedes y se verifica en revisiones. */
export function ListaInventarios(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [creando, setCreando] = useState(false);
  const escribe = puede('activo:escribir');
  const [buscar, setBuscar] = useState('');
  const [estado, setEstado] = useState('');
  const [sedes, setSedes] = useState<string[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['inventarios'],
    queryFn: () => api.get<InventarioResumen[]>('/inventarios'),
  });

  const opcionesSede = [...new Set((data ?? []).flatMap((i) => i.sedes))]
    .sort((a, b) => a.localeCompare(b, 'es'))
    .map((nombre) => ({ id: nombre, nombre }));
  const termino = buscar.trim().toLowerCase();
  const visibles = (data ?? []).filter(
    (i) =>
      (termino === '' || i.nombre.toLowerCase().includes(termino)) &&
      (estado === '' || (estado === 'CERRADO' ? i.cerradoEl !== null : i.cerradoEl === null)) &&
      (sedes.length === 0 || i.sedes.some((s) => sedes.includes(s))),
  );
  const enCurso = (data ?? []).filter((i) => i.cerradoEl === null).length;
  const cerrados = (data ?? []).length - enCurso;
  const totalRevisiones = (data ?? []).reduce((suma, i) => suma + i.revisiones, 0);
  const totalRegistros = (data ?? []).reduce((suma, i) => suma + i.activosRegistrados, 0);

  return (
    <>
      <div className="cabecera-pagina cabecera-lista">
        <div className="cabecera-lista-titulo">
          <h1>Inventarios</h1>
        </div>
        <div className="campo cabecera-lista-buscar">
          <IconoLupa />
          <input aria-label="Buscar inventarios" placeholder="Buscar inventarios" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
        </div>
        {escribe ? (
          <button type="button" className="boton" onClick={() => setCreando(true)}>
            Nuevo inventario
          </button>
        ) : null}
      </div>

      <p className="campo-nota">
        Un inventario cubre una o varias sedes y se hace en revisiones: cada pasada o jornada registra los activos
        verificados y quién estuvo a cargo de cada uno.
      </p>

      <div className="rejilla-indicadores">
        <Indicador titulo="Inventarios" valor={data?.length ?? 0} nota={`${enCurso} en curso · ${cerrados} cerrados`} />
        <Indicador titulo="En curso" valor={enCurso} tono="verde" />
        <Indicador titulo="Revisiones" valor={totalRevisiones} />
        <Indicador titulo="Activos registrados" valor={totalRegistros} />
      </div>

      <div className="filtros">
        <SelectorLista
          id="inventarios-filtro-estado"
          etiqueta="Estado"
          valor={estado}
          alCambiar={setEstado}
          opciones={[
            { id: 'EN_CURSO', nombre: 'En curso' },
            { id: 'CERRADO', nombre: 'Cerrado' },
          ]}
          textoVacio="Todos los estados"
        />
        <SelectorListaMultiple
          id="inventarios-filtro-sede"
          etiqueta="Sede"
          seleccion={sedes}
          alCambiar={setSedes}
          opciones={opcionesSede}
          textoVacio="Todas las sedes"
        />
      </div>

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {data && data.length === 0 ? (
          <Vacio
            titulo="Todavía no hay inventarios"
            detalle={escribe ? 'Cree el primero con «Nuevo inventario», por ejemplo el trimestral de sus sedes.' : undefined}
          />
        ) : null}
        {data && data.length > 0 && visibles.length === 0 ? <Vacio titulo="Ningún inventario coincide" detalle="Cambie el texto o los filtros." /> : null}
        {visibles.length > 0 ? (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Inventario</th>
                  <th>Sedes</th>
                  <th>Estado</th>
                  <th>Revisiones</th>
                  <th>Activos registrados</th>
                  <th>Creado</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((inv) => (
                  <tr key={inv.id} className="fila-clicable" onClick={() => navegar(`/activos/inventarios/${inv.id}`)}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{inv.nombre}</div>
                      {inv.descripcion ? <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{inv.descripcion}</div> : null}
                    </td>
                    <td>{inv.sedes.join(', ') || '—'}</td>
                    <td>{inv.cerradoEl ? <Etiqueta tono="gris">Cerrado</Etiqueta> : <Etiqueta tono="verde">En curso</Etiqueta>}</td>
                    <td>{inv.revisiones}</td>
                    <td>{inv.activosRegistrados}</td>
                    <td>{formatearFechaHora(inv.creadoEl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {creando ? (
        <FormularioInventario
          alCerrar={() => setCreando(false)}
          alCrear={(inv) => navegar(`/activos/inventarios/${inv.id}`)}
        />
      ) : null}
    </>
  );
}

function FormularioInventario({
  alCerrar,
  alCrear,
}: {
  alCerrar: () => void;
  alCrear: (inventario: InventarioDetalle) => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const { data: ubicaciones } = useUbicaciones();
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [sedeIds, setSedeIds] = useState<string[]>([]);

  const crear = useMutation({
    mutationFn: () =>
      api.post<InventarioDetalle>('/inventarios', {
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || undefined,
        sedeIds,
      }),
    onSuccess: async (inventario) => {
      await cliente.invalidateQueries({ queryKey: ['inventarios'] });
      alCrear(inventario);
    },
  });

  return (
    <Dialogo titulo="Nuevo inventario" alCerrar={alCerrar}>
      <div className="campo">
        <label htmlFor="inventario-nombre">Nombre</label>
        <input
          id="inventario-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Ej.: Inventario trimestral T4 2026"
          maxLength={200}
        />
      </div>
      <div className="campo">
        <label htmlFor="inventario-descripcion">Descripción (opcional)</label>
        <input id="inventario-descripcion" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} maxLength={2000} />
      </div>
      <SelectorUbicacionMultiple
        id="inventario-sedes"
        etiqueta="Sedes que abarca"
        seleccion={sedeIds}
        alCambiar={setSedeIds}
        ubicaciones={ubicaciones}
        textoVacio="Elija las sedes"
        buscador
      />
      <ErroresFormulario error={crear.error} />
      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => crear.mutate()}
        cargando={crear.isPending}
        deshabilitado={nombre.trim() === '' || sedeIds.length === 0}
        etiqueta="Crear inventario"
      />
    </Dialogo>
  );
}

/** Ficha de un inventario: sus sedes, sus revisiones y el registro de activos en cada una. */
export function DetalleInventario(): JSX.Element {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [creandoRevision, setCreandoRevision] = useState(false);
  const [registrando, setRegistrando] = useState<RevisionInventario | null>(null);
  const [viendoRegistros, setViendoRegistros] = useState<RevisionInventario | null>(null);
  const [cerrando, setCerrando] = useState(false);

  const { data: inventario, isLoading, error } = useQuery({
    queryKey: ['inventario', id],
    queryFn: () => api.get<InventarioDetalle>(`/inventarios/${id}`),
  });

  const cerrar = useMutation({
    mutationFn: () => api.post(`/inventarios/${id}/cerrar`),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['inventario', id] });
      await cliente.invalidateQueries({ queryKey: ['inventarios'] });
      setCerrando(false);
    },
  });

  if (isLoading) return <Cargando />;
  if (error || !inventario) {
    return (
      <div className="panel">
        <Vacio titulo="No se pudo abrir el inventario" detalle={mensajeError(error, 'El inventario no existe.')} />
        <p>
          <Link to="/activos/inventarios">Volver a inventarios</Link>
        </p>
      </div>
    );
  }

  const abierto = inventario.cerradoEl === null;
  const escribe = puede('activo:escribir') && abierto;

  return (
    <>
      <BotonVolver a="/activos/inventarios" etiqueta="Volver a Inventarios" />

      <div className="cabecera-pagina cabecera-lista">
        <div className="cabecera-lista-titulo">
          <h1>{inventario.nombre}</h1>
        </div>
        {escribe ? (
          <>
            <button type="button" className="boton secundario" onClick={() => setCerrando(true)}>
              Cerrar inventario
            </button>
            <button type="button" className="boton" onClick={() => setCreandoRevision(true)}>
              Nueva revisión
            </button>
          </>
        ) : null}
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-cuerpo">
          <p>
            <strong>Sedes:</strong> {inventario.sedesDetalle.map((s) => s.nombre).join(', ') || '—'}
          </p>
          {inventario.descripcion ? <p>{inventario.descripcion}</p> : null}
          <p>
            {abierto ? <Etiqueta tono="verde">En curso</Etiqueta> : <Etiqueta tono="gris">Cerrado el {formatearFechaHora(inventario.cerradoEl!)}</Etiqueta>}
          </p>
        </div>
      </div>

      <div className="panel">
        {inventario.revisionesDetalle.length === 0 ? (
          <Vacio
            titulo="Sin revisiones todavía"
            detalle={escribe ? 'Cree una revisión por cada jornada de inventario y registre los activos verificados en ella.' : undefined}
          />
        ) : (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Revisión</th>
                  <th>Fecha</th>
                  <th>Activos registrados</th>
                  <th>Nota</th>
                  <th style={{ width: 220 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {inventario.revisionesDetalle.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.nombre}</td>
                    <td>{formatearFecha(r.fecha)}</td>
                    <td>{r.activosRegistrados}</td>
                    <td>{r.nota ?? '—'}</td>
                    <td>
                      <div className="acciones-fila">
                        <button type="button" className="boton secundario pequeno" onClick={() => setViendoRegistros(r)}>
                          Ver registros
                        </button>
                        {escribe ? (
                          <button type="button" className="boton pequeno" onClick={() => setRegistrando(r)}>
                            Registrar activos
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creandoRevision ? (
        <FormularioRevision inventarioId={id} alCerrar={() => setCreandoRevision(false)} />
      ) : null}

      {registrando ? (
        <RegistrarRevision
          inventario={inventario}
          revision={registrando}
          alCerrar={() => setRegistrando(null)}
        />
      ) : null}

      {viendoRegistros ? (
        <VerRegistrosRevision
          inventarioId={id}
          revision={viendoRegistros}
          alCerrar={() => setViendoRegistros(null)}
        />
      ) : null}

      {cerrando ? (
        <DialogoConfirmar
          titulo="Cerrar inventario"
          etiqueta="Cerrar"
          mensaje={`«${inventario.nombre}» dejará de admitir revisiones y registros. Los registros ya hechos se conservan. Esta acción no se puede deshacer.`}
          cargando={cerrar.isPending}
          error={cerrar.error}
          alCerrar={() => {
            cerrar.reset();
            setCerrando(false);
          }}
          alConfirmar={() => cerrar.mutate()}
        />
      ) : null}
    </>
  );
}

function FormularioRevision({ inventarioId, alCerrar }: { inventarioId: string; alCerrar: () => void }): JSX.Element {
  const cliente = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [fecha, setFecha] = useState(hoyEnLima);
  const [nota, setNota] = useState('');

  const crear = useMutation({
    mutationFn: () =>
      api.post(`/inventarios/${inventarioId}/revisiones`, {
        nombre: nombre.trim(),
        fecha: fecha || undefined,
        nota: nota.trim() || undefined,
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['inventario', inventarioId] });
      await cliente.invalidateQueries({ queryKey: ['inventarios'] });
      alCerrar();
    },
  });

  return (
    <Dialogo titulo="Nueva revisión" alCerrar={alCerrar}>
      <div className="campo">
        <label htmlFor="revision-nombre">Nombre</label>
        <input
          id="revision-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Ej.: Revisión 1 — Sede Centro"
          maxLength={200}
        />
      </div>
      <SelectorFecha id="revision-fecha" etiqueta="Fecha de la pasada" valor={fecha} alCambiar={setFecha} />
      <div className="campo">
        <label htmlFor="revision-nota">Nota (opcional)</label>
        <input id="revision-nota" value={nota} onChange={(e) => setNota(e.target.value)} maxLength={2000} />
      </div>
      <ErroresFormulario error={crear.error} />
      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => crear.mutate()}
        cargando={crear.isPending}
        deshabilitado={nombre.trim() === '' || fecha === ''}
        etiqueta="Crear revisión"
      />
    </Dialogo>
  );
}

/**
 * Registro de activos en una revisión: se elige la sede, la persona a cargo y los
 * activos verificados. Un activo ya registrado en esta revisión no se puede volver a
 * elegir; sí se muestra dónde y quién lo verificó por última vez.
 */
function RegistrarRevision({
  inventario,
  revision,
  alCerrar,
}: {
  inventario: InventarioDetalle;
  revision: RevisionInventario;
  alCerrar: () => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const { data: usuarios } = useUsuariosOpciones();
  const [sedeId, setSedeId] = useState(inventario.sedesDetalle[0]?.id ?? '');
  const [responsableId, setResponsableId] = useState('');
  const [buscar, setBuscar] = useState('');
  const [seleccion, setSeleccion] = useState<string[]>([]);
  const [resultado, setResultado] = useState<ResultadoRegistro | null>(null);

  const { data: activos, isLoading } = useQuery({
    queryKey: ['inventario-activos', inventario.id, revision.id, sedeId],
    queryFn: () =>
      api.get<ActivoParaRevision[]>(`/inventarios/${inventario.id}/revisiones/${revision.id}/activos?ubicacionId=${sedeId}`),
    enabled: sedeId !== '',
  });

  const termino = buscar.trim().toLowerCase();
  const visibles = (activos ?? []).filter(
    (a) => termino === '' || a.nombre.toLowerCase().includes(termino) || String(a.numero).includes(termino),
  );
  const pendientes = visibles.filter((a) => !a.registradoEnRevision);
  const todosMarcados = pendientes.length > 0 && pendientes.every((a) => seleccion.includes(a.id));

  const registrar = useMutation({
    mutationFn: () =>
      api.post<ResultadoRegistro>(`/inventarios/${inventario.id}/revisiones/${revision.id}/registros`, {
        activoIds: seleccion,
        responsableId,
      }),
    onSuccess: async (res) => {
      setResultado(res);
      setSeleccion([]);
      await cliente.invalidateQueries({ queryKey: ['inventario-activos', inventario.id, revision.id] });
      await cliente.invalidateQueries({ queryKey: ['inventario', inventario.id] });
      await cliente.invalidateQueries({ queryKey: ['inventarios'] });
    },
  });

  function alternar(id: string): void {
    setSeleccion((actual) => (actual.includes(id) ? actual.filter((x) => x !== id) : [...actual, id]));
  }

  function marcarPendientes(): void {
    const ids = pendientes.map((a) => a.id);
    setSeleccion((actual) => (todosMarcados ? actual.filter((id) => !ids.includes(id)) : [...new Set([...actual, ...ids])]));
  }

  return (
    <Dialogo
      titulo={`Registrar activos · ${revision.nombre}`}
      ancho={900}
      altoFijo
      alCerrar={alCerrar}
    >
      <div className="rejilla-campos">
        <SelectorLista
          id="registro-sede"
          etiqueta="Sede"
          valor={sedeId}
          alCambiar={(v) => {
            setSedeId(v);
            setResponsableId('');
            setSeleccion([]);
            setResultado(null);
          }}
          opciones={inventario.sedesDetalle.map((s) => ({ id: s.id, nombre: s.nombre }))}
          requerido
          buscador
        />
        <SelectorLista
          id="registro-responsable"
          etiqueta="Quién estuvo a cargo"
          valor={responsableId}
          alCambiar={setResponsableId}
          opciones={(usuarios ?? []).map((u) => ({ id: u.id, nombre: u.nombre }))}
          textoVacio="Elija un usuario"
          requerido
          buscador
        />
      </div>

      <div className="campo cabecera-lista-buscar" style={{ margin: '12px 0' }}>
        <IconoLupa />
        <input aria-label="Buscar activo" placeholder="Buscar por nombre o número" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <span>
          {plural(visibles.length, 'activo', 'activos')} · {plural(pendientes.length, 'pendiente', 'pendientes')} en esta revisión
        </span>
        <button type="button" className="boton secundario pequeno" onClick={marcarPendientes} disabled={pendientes.length === 0}>
          {todosMarcados ? 'Quitar pendientes' : 'Marcar pendientes'}
        </button>
      </div>

      {resultado ? (
        <p className="campo-nota">
          Se registraron {plural(resultado.registrados, 'activo', 'activos')}
          {resultado.yaRegistrados > 0 ? ` (${resultado.yaRegistrados} ya estaban en esta revisión)` : ''}.
        </p>
      ) : null}

      {isLoading ? <Cargando /> : null}
      {activos && visibles.length === 0 ? <Vacio titulo="Sin activos para mostrar" detalle="Revise la sede o el texto de búsqueda." /> : null}

      <div className="tabla-contenedor" style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table className="tabla">
          <tbody>
            {visibles.map((a) => (
              <tr key={a.id}>
                <td style={{ width: 40 }}>
                  <CasillaVerificacion
                    marcada={a.registradoEnRevision || seleccion.includes(a.id)}
                    alCambiar={() => alternar(a.id)}
                    deshabilitada={a.registradoEnRevision}
                    etiqueta={`Registrar activo #${a.numero}`}
                  />
                </td>
                <td className="numero-registro">#{a.numero}</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{a.nombre}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{a.ubicacionNombre ?? 'Sin ubicación'}</div>
                </td>
                <td style={{ fontSize: 12.5 }}>
                  {a.registradoEnRevision ? (
                    <Etiqueta tono="verde">Registrado en esta revisión</Etiqueta>
                  ) : a.ultimoRegistro ? (
                    <span style={{ color: 'var(--texto-tenue)' }}>
                      Último: {a.ultimoRegistro.inventarioNombre} · {a.ultimoRegistro.revisionNombre} ·{' '}
                      {formatearFecha(a.ultimoRegistro.fecha)} · {a.ultimoRegistro.responsableNombre}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--texto-tenue)' }}>Sin verificar</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ErroresFormulario error={registrar.error} />
      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => registrar.mutate()}
        cargando={registrar.isPending}
        deshabilitado={seleccion.length === 0 || responsableId === ''}
        etiqueta={seleccion.length > 0 ? `Registrar ${plural(seleccion.length, 'activo', 'activos')}` : 'Registrar'}
      />
    </Dialogo>
  );
}

function VerRegistrosRevision({
  inventarioId,
  revision,
  alCerrar,
}: {
  inventarioId: string;
  revision: RevisionInventario;
  alCerrar: () => void;
}): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['inventario-registros', inventarioId, revision.id],
    queryFn: () => api.get<RegistroDeRevision[]>(`/inventarios/${inventarioId}/revisiones/${revision.id}/registros`),
  });

  return (
    <Dialogo titulo={`Registros · ${revision.nombre}`} ancho={800} altoFijo alCerrar={alCerrar}>
      {isLoading ? <Cargando /> : null}
      <ErroresFormulario error={error} />
      {data && data.length === 0 ? <Vacio titulo="Nada registrado en esta revisión" /> : null}
      {data && data.length > 0 ? (
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Activo</th>
                <th>Ubicación</th>
                <th>Quién estuvo a cargo</th>
                <th>Registrado</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.id}>
                  <td>
                    <span className="numero-registro">#{r.activoNumero}</span> {r.activoNombre}
                    {r.nota ? <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{r.nota}</div> : null}
                  </td>
                  <td>{r.ubicacionNombre ?? '—'}</td>
                  <td>{r.responsableNombre}</td>
                  <td>{formatearFechaHora(r.registradoEl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Dialogo>
  );
}

interface RegistroActivoInventario {
  id: string;
  inventarioId: string;
  inventarioNombre: string;
  revisionId: string;
  revisionNombre: string;
  fecha: string;
  responsableNombre: string;
  nota: string | null;
  registradoEl: string;
}

/** Registros de inventario del activo, el más reciente primero. Lo comparten la ficha y el panel de historial. */
export function useInventariosDelActivo(activoId: string) {
  return useQuery({
    queryKey: ['activo-inventarios', activoId],
    queryFn: () => api.get<RegistroActivoInventario[]>(`/inventarios/por-activo/${activoId}`),
  });
}

/** Panel de la ficha del activo: en qué inventarios y revisiones se verificó, y quién estuvo a cargo. */
export function HistorialInventarioActivo({ activoId }: { activoId: string }): JSX.Element {
  const { data, isLoading } = useInventariosDelActivo(activoId);

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Inventario</h2>
      </div>
      {isLoading ? <Cargando /> : null}
      {data && data.length === 0 ? <Vacio titulo="Sin verificaciones de inventario" detalle="Aparecerá aquí al registrarlo en una revisión." /> : null}
      {data && data.length > 0 ? (
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Inventario</th>
                <th>Revisión</th>
                <th>Fecha</th>
                <th>Quién estuvo a cargo</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/activos/inventarios/${r.inventarioId}`}>{r.inventarioNombre}</Link>
                  </td>
                  <td>{r.revisionNombre}</td>
                  <td>{formatearFecha(r.fecha)}</td>
                  <td>{r.responsableNombre}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
